// src/pages/healthcare/results/testOrderComponents/BloodResultsTable.tsx
//
// Blood-test results for the test-order modal, rendered as a single flat table
// (Name / Value / Range / Range bar). Replaces BloodResultsAccordion — both
// view modes below render through this one component:
//
//  - "report" mode (default): analytes in true source-report order
//    (`report_sequence`), with `report_group` shown as a section heading where
//    present. Legacy TestResult rows with no `report_sequence` fall back to the
//    existing array order as-is.
//  - "grouped" mode: same catalogue-driven category grouping the accordion and
//    the blood-wheels modal use (flattenBloodTestResults + buildBloodWheelData),
//    ordered by the catalogue's sort_order (not alphabetically).
//
// 86d3me2dp (frontend) — sibling of backend subtask 86d3kwwr9.

import React, { useMemo } from "react";
import { Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";

import { EmptyState } from "../../../../components/shared";
import ResultRangeBar from "../../../gene.codes/bloods/BloodAnalysiscomponents/ResultRangeBar";
import RangeSourceTag from "../../../../components/blood/RangeSourceTag";

import { bloodTestFromOrder } from "../../../../utils/geneCodes/bloodTestShape";
import { buildBloodWheelData, BloodWheelCategory } from "../../../../utils/geneCodes/buildBloodWheelData";
import { calculateResultSeverity, resolveResultRange, ResultSeverityData } from "../../../../utils/geneCodes/bloodResultUtils";
import { RangeContext } from "../../../../utils/geneCodes/referenceRange";
import { useBloodRangeContext, RangePatient } from "../../../../hooks/useBloodRangeContext";

import { Test } from "../../../../types/healthcare/TestTypes";
import { BloodTestResult } from "../../../../types/lab/BloodTestTypes";

const { Text } = Typography;

export type BloodResultsViewMode = "report" | "grouped";

interface BloodResultsTableProps {
	currentOrder: Partial<Test>;
	mode: BloodResultsViewMode;
	patient?: RangePatient | null;
}

const toTitleCase = (str: string): string =>
	str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// A table row is either a full-width section heading or an analyte data row.
// Kept as a discriminated union so a single 4-column Table can render both
// view modes without ever changing the column count.
type TableRow =
	| { rowType: "group"; rowKey: string; label: string; color: string | null }
	| { rowType: "result"; rowKey: string; result: BloodTestResult; severity: ResultSeverityData };

const groupRow = (rowKey: string, label: string, color: string | null): TableRow => ({
	rowType: "group",
	rowKey,
	label,
	color,
});

// Severity is resolved here, once per row — the Name and Value cells both need it and
// Ant Design re-runs every cell renderer on each scroll.
const resultRow = (result: BloodTestResult, rangeContext: RangeContext): TableRow => ({
	rowType: "result",
	rowKey: `result-${result.id}`,
	result,
	severity: calculateResultSeverity(result, rangeContext),
});

const BloodResultsTable: React.FC<BloodResultsTableProps> = ({ currentOrder, mode, patient }) => {
	const results = useMemo<BloodTestResult[]>(() => bloodTestFromOrder(currentOrder)?.results ?? [], [currentOrder]);

	// The catalogue drives Grouped-mode buckets and, with the patient, every row's band (RR-1)
	const { catalogue, rangeContext, isLoading: catalogueLoading } = useBloodRangeContext(patient);

	// Catalogue-driven buckets, same fallback as the old accordion: if the catalogue is
	// unavailable/empty, group by the analyte's test_category instead so nothing is dropped.
	const categories = useMemo<BloodWheelCategory[]>(() => {
		if (mode !== "grouped" || results.length === 0) return [];

		if (catalogue.length > 0) {
			return buildBloodWheelData(results, catalogue);
		}

		const fallback = new Map<string, BloodTestResult[]>();
		results.forEach((result) => {
			const category = result.test_category || "Other / Ungrouped";
			if (!fallback.has(category)) fallback.set(category, []);
			fallback.get(category)!.push(result);
		});

		return Array.from(fallback.entries())
			.map(([name, bucket]) => ({
				name,
				color: null,
				sort_order: name === "Other / Ungrouped" ? Number.MAX_SAFE_INTEGER : 0,
				results: [...bucket].sort((a, b) => a.sort_order - b.sort_order),
			}))
			.sort((a, b) => {
				if (a.name === "Other / Ungrouped") return 1;
				if (b.name === "Other / Ungrouped") return -1;
				return a.name.localeCompare(b.name);
			});
	}, [mode, results, catalogue]);

	// Report-order analytes: sorted by `report_sequence` (the true physical position
	// in the source report). Legacy fallback — if any analyte is missing
	// `report_sequence` (ingested before 86d3kwwr9 shipped), keep the existing
	// array order as-is rather than guessing at a mixed sort.
	const reportOrderedResults = useMemo<BloodTestResult[]>(() => {
		if (mode !== "report") return [];
		const allHaveSequence = results.every((r) => r.report_sequence !== undefined && r.report_sequence !== null);
		if (!allHaveSequence) return results;
		return [...results].sort((a, b) => (a.report_sequence ?? 0) - (b.report_sequence ?? 0));
	}, [mode, results]);

	const rows = useMemo<TableRow[]>(() => {
		if (mode === "grouped") {
			const out: TableRow[] = [];
			categories.forEach((category) => {
				if (category.results.length === 0) return;
				out.push(groupRow(`group-${category.name}`, category.name, category.color));
				category.results.forEach((result) => out.push(resultRow(result, rangeContext)));
			});
			return out;
		}

		// Report order: insert a section heading whenever `report_group` changes
		// from the previous row. Rows with no report_group (null/undefined) render
		// with no heading at all.
		const out: TableRow[] = [];
		let lastGroup: string | null = null;
		reportOrderedResults.forEach((result) => {
			const group = result.report_group ?? null;
			if (group && group !== lastGroup) {
				out.push(groupRow(`group-${group}-${result.id}`, group, null));
			}
			lastGroup = group;
			out.push(resultRow(result, rangeContext));
		});
		return out;
	}, [mode, categories, reportOrderedResults, rangeContext]);

	const columns: ColumnsType<TableRow> = [
		{
			title: "Name",
			key: "name",
			onCell: (row) => (row.rowType === "group" ? { colSpan: 4 } : {}),
			render: (_: unknown, row: TableRow) => {
				if (row.rowType === "group") {
					return (
						<div style={{ display: "flex", alignItems: "center", gap: 8, background: row.color }}>
							{row.color && <span style={{ width: 10, height: 10, borderRadius: "50%", background: row.color, display: "inline-block", flexShrink: 0 }} />}
							<Text style={{ fontSize: 12, fontWeight: 600 }}>{row.label}</Text>
						</div>
					);
				}
				const name = toTitleCase(row.result.test_name ?? "");
				const nameText = <Text style={{ fontSize: 12, fontWeight: 500 }} title={name}>{name}</Text>;
				if (!row.severity.isAbnormal) return nameText;
				return (
					<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
						<span title={row.severity.label} style={{ width: 10, height: 10, borderRadius: "50%", background: row.severity.color, display: "inline-block", flexShrink: 0 }} />
						{nameText}
					</div>
				);
			},
		},
		{
			title: "Value",
			key: "value",
			onCell: (row) => (row.rowType === "group" ? { colSpan: 0 } : {}),
			render: (_: unknown, row: TableRow) => {
				if (row.rowType === "group") return null;
				return (
					<Text style={{ fontSize: 12, ...(row.severity.isAbnormal ? { color: row.severity.color, fontWeight: 700 } : {}) }}>
						{row.result.result_value}
						{row.result.units ? ` ${row.result.units}` : ""}
					</Text>
				);
			},
		},
		{
			title: "Range",
			key: "range",
			onCell: (row) => (row.rowType === "group" ? { colSpan: 0 } : {}),
			render: (_: unknown, row: TableRow) => {
				if (row.rowType === "group") return null;
				const { display } = resolveResultRange(row.result, rangeContext);
				return (
					<div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
						<Text type="secondary" style={{ fontSize: 12 }}>{display || "—"}</Text>
						<RangeSourceTag source={row.severity.source} labFlag={row.result.lab_flag} />
					</div>
				);
			},
		},
		{
			title: "Range bar",
			key: "rangeBar",
			onCell: (row) => (row.rowType === "group" ? { colSpan: 0 } : {}),
			render: (_: unknown, row: TableRow) => {
				if (row.rowType === "group") return null;
				return <ResultRangeBar result={row.result} rangeContext={rangeContext} compact showLabels={false} height={8} />;
			},
		},
	];

	if (results.length === 0) {
		return (
			<EmptyState
				title="No results available"
				description="Results will appear here once the test is completed and analysed."
			/>
		);
	}

	return (
		<Table<TableRow>
			dataSource={rows}
			columns={columns}
			rowKey={(row) => row.rowKey}
			pagination={false}
			size="small"
			showHeader
			loading={catalogueLoading}
		/>
	);
};

export default BloodResultsTable;
